import { deviationPct } from "@opus-dt/shared-types";
import { DIMENSION_LABELS } from "@/features/simulations/applied/applied-scenario-labels";
import {
  METRIC_LABELS,
  PERCENT_METRICS,
  type AdherenceDimensionKey,
  type AppliedScenarioMetricKey,
} from "@/types/applied-scenario.types";
import type { ProposalEvidence } from "@/types/closed-loop.types";

interface EvidencePanelProps {
  evidence: ProposalEvidence;
  /** `true` quando a interferência externa não foi consultada — ramo 3b, o caso da fila. */
  needsDetail: boolean;
}

// Spec 077 (FR-006 / FR-016) — a EVIDÊNCIA da proposta, em termos do domínio.
//
// Uma causa sugerida sem o fato que a sustenta é um palpite com aparência de autoridade: quem julga
// precisa poder discordar olhando o mesmo número que a regra olhou. Por isso cada variante mostra o
// dado bruto — índice contra limiar, realizado contra previsto e faixa, ordens intrusas nomeadas.

/** `—` para o que não foi medido. Nunca `0`: as duas coisas levam a ações opostas. */
function fmt(value: number | null, suffix = ""): string {
  return value === null
    ? "—"
    : `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}${suffix}`;
}

/**
 * O valor de uma MÉTRICA, com a unidade dela — a mesma regra da tabela de apuração logo acima nesta
 * tela. Sem isto o painel escrevia `78,5` onde a tabela escreve `78,5%`, e `4200` onde ela escreve
 * `4.200`: dois números diferentes para o mesmo fato, a centímetros um do outro.
 */
function fmtMetric(value: number | null, metric: AppliedScenarioMetricKey): string {
  return fmt(value, PERCENT_METRICS.has(metric) ? "%" : "");
}

function Frame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2 rounded-md border border-border-subtle bg-bg-muted-rd p-3">
      <p className="text-[12px] font-medium text-text-primary">{title}</p>
      {children}
    </div>
  );
}

export function EvidencePanel({ evidence, needsDetail }: EvidencePanelProps) {
  if (evidence.kind === "adherence") {
    return (
      <Frame title="O que sustenta esta proposta">
        <p className="text-[12px] text-text-secondary">
          Índice de aderência congelado{" "}
          <span className="tabular-nums text-text-primary">{fmt(evidence.index, "%")}</span> contra
          o limiar declarado{" "}
          <span className="tabular-nums text-text-primary">{fmt(evidence.threshold, "%")}</span>.
          {evidence.index === null
            ? " Nenhuma dimensão era mensurável — e o que não foi medido também fica fora da estatística."
            : null}
        </p>
        {evidence.driverDimension ? (
          <p className="text-[12px] text-text-secondary">
            {/* O NOME da dimensão, não a chave: o painel de aderência, a centímetros daqui na
                mesma tela, escreve "Turnos" — "shifts" se lê como outro fato. */}
            Dimensão responsável pelo índice:{" "}
            <span className="text-text-primary">
              {DIMENSION_LABELS[evidence.driverDimension as AdherenceDimensionKey] ??
                evidence.driverDimension}
            </span>
            .
          </p>
        ) : null}
      </Frame>
    );
  }

  if (evidence.kind === "tolerance") {
    // FR-016 — a de MAIOR desvio relativo vem destacada: é por ela que a conversa começa.
    const top = evidence.metrics.reduce<{ metric: string; pct: number } | null>((best, m) => {
      const pct = deviationPct(m.predicted, m.realized);
      if (pct === null) return best;
      return best === null || pct > best.pct ? { metric: m.metric, pct } : best;
    }, null);

    return (
      <Frame title="O que sustenta esta proposta">
        <ul className="space-y-1">
          {evidence.metrics.map((m) => (
            <li key={m.metric} className="flex flex-wrap items-baseline gap-x-2 text-[12px]">
              <span className="text-text-primary">{METRIC_LABELS[m.metric] ?? m.metric}</span>
              <span className="tabular-nums text-text-secondary">
                previsto {fmtMetric(m.predicted, m.metric)} · realizado{" "}
                {fmtMetric(m.realized, m.metric)} · faixa {fmt(m.tolerancePct, "%")}
              </span>
              {top?.metric === m.metric ? (
                <span className="rounded-full border border-border-subtle bg-bg-surface px-2 py-px text-[11px] text-text-secondary">
                  maior desvio · {fmt(top.pct, "%")}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </Frame>
    );
  }

  if (evidence.kind === "interference") {
    return (
      <Frame title="O que sustenta esta proposta">
        <p className="text-[12px] text-text-secondary">
          Produziram nesta linha, dentro da vigência, sem estar no escopo comprometido:
        </p>
        <ul className="space-y-1">
          {evidence.orders.map((o) => (
            <li key={o.productionOrderId} className="text-[12px] text-text-secondary">
              <span className="text-text-primary">{o.code ?? "OP sem código"}</span> ·{" "}
              <span className="tabular-nums">{o.producedQty}</span> peças
            </li>
          ))}
        </ul>
      </Frame>
    );
  }

  // `none` — e as duas leituras dele são MUITO diferentes (FR-005). Dizer "nenhuma causa" nas duas
  // seria afirmar o que ninguém verificou no primeiro caso e esconder uma limitação no segundo.
  return (
    <Frame title="O sistema não propôs uma causa">
      <p className="text-[12px] text-text-secondary">
        {needsDetail
          ? "Esta leitura não verificou se houve produção fora do escopo comprometido, então a regra não pôde ir até o fim. Abra a apuração do compromisso para ver o quadro completo."
          : "A execução seguiu o combinado, o resultado ficou fora da faixa declarada e não houve produção intrusa na janela. Nenhuma das regras se aplica — a partir daqui é julgamento de quem conhece a operação."}
      </p>
    </Frame>
  );
}
